/**
 * Formulários dinâmicos por produto — lado do ADMIN (AN-6 §1.1 e §6, L5).
 *
 * O schema é o MESMO documento que o consultor responde: os tipos dele vêm de
 * `types/formulario.ts` (dono DEV-L) e são reexportados aqui — integração da Onda 4. Aqui ficam
 * só os tipos que a tela de cadastro usa: versões, histórico, o envelope do
 * `GET /v1/oportunidades/formularios/produto/{produtoId}?etapa=` e os rótulos do editor.
 */
import type {
  CondicaoCampo,
  EtapaFormulario,
  MarcoExigencia,
  OpcaoCampo,
  OperadorCondicao,
  SchemaFormulario,
  TipoCampoFormulario,
  ValidacaoGrupo,
} from '@/types/formulario'

export type {
  EtapaFormulario,
  ItemFormulario,
  MarcoExigencia,
  OperadorCondicao,
  SchemaFormulario,
  TipoCampoFormulario,
} from '@/types/formulario'

/**
 * `condition` (visibilidade) e `requiredWhen` (obrigatoriedade condicional). UM_DE usa
 * `valores: string[]` (canônico do backend, `CondicaoFormulario.java`); IGUAL/DIFERENTE/
 * MAIOR_QUE usam `valor` (texto; SIM_NAO grava "true"/"false"); PREENCHIDO não usa valor.
 */
export type CondicaoFormulario = CondicaoCampo
export type OpcaoFormulario = OpcaoCampo
/** `validacoes[SOMA]` de um grupo repetível, por marco. */
export type ValidacaoSomaFormulario = ValidacaoGrupo

/**
 * Ciclo (V55 + V64/AN-PP6 §3): RASCUNHO → PENDENTE_APROVACAO (proposta) → PUBLICADA (aprovada por OUTRA pessoa) →
 * SUBSTITUIDA; a proposta pode ser REJEITADA. LEGADO = bloco fixo V39.
 */
export type StatusFormulario =
  | 'RASCUNHO'
  | 'PENDENTE_APROVACAO'
  | 'PUBLICADA'
  | 'SUBSTITUIDA'
  | 'REJEITADA'
  | 'LEGADO'

/** Uma linha de `formularios_produto` (V55). */
export interface VersaoFormularioProduto {
  id: string
  produtoId: string | null
  etapa: EtapaFormulario
  versao: number
  status: StatusFormulario
  /** Pode vir ausente no histórico (resumo); aí a tela busca `GET /versoes/{id}`. */
  schema?: SchemaFormulario | null
  schemaHash?: string | null
  criadoPor?: string | null
  criadoEm?: string | null
  publicadoPor?: string | null
  publicadoEm?: string | null
  // ── V64 (AN-PP6 §3): publicação com dupla aprovação ──
  owner?: string | null
  /** `sub` de quem enviou para aprovação. */
  propostoPor?: string | null
  propostoEm?: string | null
  /** `sub` de quem aprovou — sempre diferente de `propostoPor`. */
  aprovadoPor?: string | null
  aprovadoEm?: string | null
  rejeitadoPor?: string | null
  rejeitadoEm?: string | null
  motivoRejeicao?: string | null
  legado?: boolean
  /** Volta em aprovar/rejeitar (409 `CONFLITO_VERSAO`). */
  versaoRegistro?: number | null
}

/** Corpo de aprovar/rejeitar (V64); quem decide é o token. */
export interface DecisaoFormularioRequest {
  motivo?: string
  versaoRegistro?: number | null
  /** Hash do schema revisado (revisão PP6-valr, A1): obrigatório na aprovação; diferente → 409 `PROPOSTA_DIVERGENTE`. */
  schemaHash?: string | null
}

/** `GET /v1/oportunidades/formularios/produto/{produtoId}?etapa=` (§6). */
export interface FormulariosProdutoEtapa {
  vigente: VersaoFormularioProduto | null
  rascunho: VersaoFormularioProduto | null
  historico: VersaoFormularioProduto[]
  /** V64: proposta aguardando aprovação de OUTRA pessoa (nula se não houver). */
  pendente?: VersaoFormularioProduto | null
}

export const ETAPA_FORMULARIO_LABEL: Record<EtapaFormulario, string> = {
  CADASTRO: 'Cadastro',
  ESTRUTURACAO: 'Estruturação',
}

export const MARCO_EXIGENCIA_LABEL: Record<MarcoExigencia, string> = {
  ENVIO_ANALISE: 'Envio para análise',
  ENCAMINHAMENTO_PARCEIRO: 'Encaminhamento ao parceiro',
}

export const TIPO_CAMPO_LABEL: Record<TipoCampoFormulario, string> = {
  TEXTO: 'Texto',
  TEXTO_LONGO: 'Texto longo',
  NUMERO: 'Número',
  MOEDA: 'Moeda (R$)',
  PERCENTUAL: 'Percentual (%)',
  DATA: 'Data',
  SIM_NAO: 'Sim/Não',
  OPCAO: 'Opções',
  CPF: 'CPF',
  CNPJ: 'CNPJ',
  EMAIL: 'E-mail',
  TELEFONE: 'Telefone',
  GRUPO: 'Grupo de campos',
}

export const OPERADOR_CONDICAO_LABEL: Record<OperadorCondicao, string> = {
  IGUAL: 'é igual a',
  DIFERENTE: 'é diferente de',
  UM_DE: 'é um de',
  PREENCHIDO: 'está preenchido',
  MAIOR_QUE: 'é maior que',
}

export const STATUS_FORMULARIO_LABEL: Record<StatusFormulario, string> = {
  RASCUNHO: 'Rascunho',
  PENDENTE_APROVACAO: 'Aguardando aprovação',
  PUBLICADA: 'Vigente',
  SUBSTITUIDA: 'Substituída',
  REJEITADA: 'Rejeitada',
  LEGADO: 'Legado',
}
